import { c as _c } from "react/compiler-runtime";
import type { ChangelogPost } from '@onefootprint/changelog';
import { IcoArrowUpRight16, IcoChevronLeftBig16 } from '@onefootprint/icons';
import { CodeBlock, Dialog, MarkdownText } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { format } from 'date-fns';
import Image from 'next/image';
import { type ComponentProps, isValidElement, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
type WhatsNewDialogProps = {
  onClose: () => void;
  open: boolean;
  posts: ChangelogPost[];
};
const DIGEST_COUNT = 2;
const CHANGELOG_URL = 'https://docs.onefootprint.com/changelog';
const cardClassName = 'flex flex-col overflow-hidden border border-solid rounded border-tertiary';
const revealClassName = 'animate-context-explorer-item motion-reduce:animate-none';
const dateClassName = 'text-label-3 text-tertiary uppercase tracking-wide';
type CardProps = {
  post: ChangelogPost;
  style?: React.CSSProperties;
};
type DigestCardProps = CardProps & {
  onSelect: (post: ChangelogPost) => void;
};

// MarkdownText leaves code blocks unstyled and indents quotes 48px; posts use the design system's
// CodeBlock and the agent chat's quote instead.
const postMarkdownComponents: ComponentProps<typeof MarkdownText>['components'] = {
  pre: ({
    children
  }) => {
    if (!isValidElement<{
      className?: string;
      children?: ReactNode;
    }>(children)) return <pre>{children}</pre>;
    const language = children.props.className?.replace('language-', '') ?? 'text';
    return <div className="my-4">
        <CodeBlock language={language}>{String(children.props.children).replace(/\n$/, '')}</CodeBlock>
      </div>;
  },
  blockquote: ({
    children
  }) => <blockquote className="border-l-2 border-tertiary pl-3">{children}</blockquote>
};
const PostDate = t0 => {
  const $ = _c(4);
  const {
    post
  } = t0;
  let t1;
  if ($[0] !== post.publishedAt) {
    t1 = format(post.publishedAt, "MMM d, yyyy");
    $[0] = post.publishedAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <p className={dateClassName}>{t1}</p>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const HeroCard = t0 => {
  const $ = _c(18);
  const {
    post,
    style
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cx(cardClassName, revealClassName);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== post.featureImageUrl || $[2] !== post.title) {
    t2 = post.featureImageUrl && <Image src={post.featureImageUrl} alt={post.title} width={1200} height={675} className="object-cover w-full aspect-[16/9]" />;
    $[1] = post.featureImageUrl;
    $[2] = post.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== post) {
    t3 = <PostDate post={post} />;
    $[4] = post;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== post.title) {
    t4 = <h3 className="text-heading-3 text-primary tracking-tight [text-wrap:balance]">{post.title}</h3>;
    $[6] = post.title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== post.bodyMd) {
    t5 = <div className="max-w-[68ch] text-body-2 text-secondary [&_:is(h1,h2,h3,h4,h5,h6):not(:first-child)]:mt-6 [&_:is(p,ul,ol,blockquote)+:is(p,ul,ol,blockquote)]:mt-2 [&_li]:my-0 [&_li+li]:mt-2"><MarkdownText components={postMarkdownComponents}>{post.bodyMd}</MarkdownText></div>;
    $[8] = post.bodyMd;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = <div className="flex flex-col gap-2 p-6">{t3}{t4}{t5}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== style || $[15] !== t2 || $[16] !== t6) {
    t7 = <article style={style} className={t1}>{t2}{t6}</article>;
    $[14] = style;
    $[15] = t2;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
const DigestCard = t0 => {
  const $ = _c(18);
  const {
    post,
    style,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== onSelect || $[1] !== post) {
    t1 = () => onSelect(post);
    $[0] = onSelect;
    $[1] = post;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cx(cardClassName, revealClassName, "text-left cursor-pointer hover:bg-hover");
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== post) {
    t3 = <PostDate post={post} />;
    $[4] = post;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== post.title) {
    t4 = <h4 className="text-label-2 text-primary">{post.title}</h4>;
    $[6] = post.title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== post.excerpt) {
    t5 = <p className="text-body-3 text-secondary line-clamp-2">{post.excerpt}</p>;
    $[8] = post.excerpt;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = <div className="flex flex-col gap-1 p-3 min-w-0">{t3}{t4}{t5}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== style || $[15] !== t1 || $[16] !== t6) {
    t7 = <button type="button" onClick={t1} style={style} className={t2}>{t6}</button>;
    $[14] = style;
    $[15] = t1;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
const WhatsNewDialog = t0 => {
  const $ = _c(50);
  const {
    open,
    onClose,
    posts
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "components.privateLayout.nav"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  const [selectedPost, setSelectedPost] = useState(null);
  let t2;
  if ($[1] !== posts) {
    t2 = posts.find(_temp);
    $[1] = posts;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const hero = t2;
  let T0;
  let t10;
  let t11;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== hero || $[4] !== onClose || $[5] !== open || $[6] !== posts || $[7] !== selectedPost || $[8] !== t) {
    let t12;
    if ($[19] !== hero) {
      t12 = post_0 => post_0 !== hero;
      $[19] = hero;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    const digests = posts.filter(t12).slice(0, DIGEST_COUNT);
    const digestDelay = hero ? 1 : 0;
    T0 = Dialog;
    t7 = open;
    if ($[21] !== onClose) {
      t8 = () => {
        setSelectedPost(null);
        onClose();
      };
      $[21] = onClose;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    if ($[23] !== t) {
      t9 = t(_temp2);
      $[23] = t;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    if ($[25] !== selectedPost || $[26] !== t) {
      t10 = selectedPost ? {
        ariaLabel: t(_temp3),
        component: IcoChevronLeftBig16,
        onClick: () => setSelectedPost(null)
      } : undefined;
      $[25] = selectedPost;
      $[26] = t;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t13;
    if ($[28] !== t) {
      t13 = t(_temp4);
      $[28] = t;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    if ($[30] !== t13) {
      t11 = {
        label: t13,
        iconComponent: IcoArrowUpRight16,
        iconPosition: "right",
        onClick: _temp5
      };
      $[30] = t13;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    t3 = "flex flex-col min-h-[400px] gap-5";
    if ($[32] !== selectedPost) {
      t4 = selectedPost && <HeroCard post={selectedPost} />;
      $[32] = selectedPost;
      $[33] = t4;
    } else {
      t4 = $[33];
    }
    if ($[34] !== hero || $[35] !== selectedPost) {
      t5 = !selectedPost && hero && <HeroCard post={hero} />;
      $[34] = hero;
      $[35] = selectedPost;
      $[36] = t5;
    } else {
      t5 = $[36];
    }
    t6 = !selectedPost && digests.length > 0 && <section style={{
      animationDelay: `${digestDelay * 80}ms`
    }} className={cx("flex flex-col gap-3", revealClassName)}><h3 className={dateClassName}>{t(_temp6)}</h3><div className="flex flex-col gap-2">{digests.map((post_1, j) => <DigestCard key={post_1.slug} post={post_1} onSelect={setSelectedPost} style={{
          animationDelay: `${(digestDelay + 1 + j) * 80}ms`
        }} />)}</div></section>;
    $[3] = hero;
    $[4] = onClose;
    $[5] = open;
    $[6] = posts;
    $[7] = selectedPost;
    $[8] = t;
    $[9] = T0;
    $[10] = t10;
    $[11] = t11;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    T0 = $[9];
    t10 = $[10];
    t11 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t12;
  if ($[37] !== t3 || $[38] !== t4 || $[39] !== t5 || $[40] !== t6) {
    t12 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[37] = t3;
    $[38] = t4;
    $[39] = t5;
    $[40] = t6;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== T0 || $[43] !== t10 || $[44] !== t11 || $[45] !== t12 || $[46] !== t7 || $[47] !== t8 || $[48] !== t9) {
    t13 = <T0 open={t7} onClose={t8} title={t9} headerIcon={t10} linkButton={t11}>{t12}</T0>;
    $[42] = T0;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
    $[46] = t7;
    $[47] = t8;
    $[48] = t9;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  return t13;
};
export default WhatsNewDialog;
function _temp(post) {
  return post.featureImageUrl;
}
function _temp2($) {
  return $.whatsNew.title;
}
function _temp3($_0) {
  return $_0.whatsNew.back;
}
function _temp4($_1) {
  return $_1.whatsNew.viewAll;
}
function _temp5() {
  return window.open(CHANGELOG_URL, "_blank", "noopener");
}
function _temp6($_2) {
  return $_2.whatsNew.otherRecentUpdates;
}